import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { Plus, X, Trash2 } from "lucide-react";

const emptyForm = {
  slug: "", title: "", excerpt: "", cover_image: "",
  body_html: "", tags: [], product_ids: [], published: false, author: "Dabkazari Atelier",
};

function ProductPicker({ selected, onChange }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [chosen, setChosen] = React.useState([]); // full product objects

  // hydrate chosen from selected IDs on mount
  React.useEffect(() => {
    if (selected?.length && chosen.length !== selected.length) {
      (async () => {
        const { data } = await api.get(`/admin/products/search?limit=200`);
        const map = new Map(data.map((p) => [p.product_id, p]));
        setChosen(selected.map((id) => map.get(id)).filter(Boolean));
      })();
    }
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/products/search?q=${encodeURIComponent(q)}&limit=10`);
        setResults(data);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const add = (p) => {
    if (chosen.find((c) => c.product_id === p.product_id)) return;
    const next = [...chosen, p];
    setChosen(next);
    onChange(next.map((c) => c.product_id));
    setQ("");
    setResults([]);
  };
  const remove = (id) => {
    const next = chosen.filter((c) => c.product_id !== id);
    setChosen(next);
    onChange(next.map((c) => c.product_id));
  };

  return (
    <div data-testid="admin-blog-products">
      <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mb-2">Shop the Story — link products</p>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search products by name…"
        className="w-full border border-black/10 px-3 py-2 text-sm"
        data-testid="admin-blog-product-search"
      />
      {results.length > 0 && (
        <ul className="mt-2 border border-black/10 max-h-56 overflow-y-auto bg-white">
          {results.map((p) => (
            <li
              key={p.product_id}
              onClick={() => add(p)}
              className="px-3 py-2 flex items-center gap-3 hover:bg-[#F3EFEA] cursor-pointer text-sm"
              data-testid={`admin-blog-product-opt-${p.slug}`}
            >
              <img src={p.images?.[0]} alt="" className="w-8 h-10 object-cover" />
              <span className="flex-1">{p.name}</span>
              <span className="text-[11px] text-[#7A7A7A]">₹{Math.round(p.price_inr).toLocaleString("en-IN")}</span>
            </li>
          ))}
        </ul>
      )}
      {chosen.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {chosen.map((p) => (
            <span key={p.product_id} className="inline-flex items-center gap-2 bg-[#F3EFEA] border border-[#BCA37F]/50 px-2 py-1 text-xs" data-testid={`admin-blog-product-chip-${p.slug}`}>
              {p.name}
              <button type="button" onClick={() => remove(p.product_id)} className="text-[#5C1A1B]" aria-label="Remove product">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const { data } = await api.get("/admin/blog");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditingId(null); setShow(true); };
  const openEdit = (p) => {
    setForm({
      slug: p.slug, title: p.title, excerpt: p.excerpt || "", cover_image: p.cover_image || "",
      body_html: p.body_html || "", tags: p.tags || [], product_ids: p.product_ids || [],
      published: !!p.published, author: p.author || "Dabkazari Atelier",
    });
    setEditingId(p.post_id); setShow(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/blog/${editingId}`, form);
        toast.success("Post updated.");
      } else {
        await api.post("/admin/blog", form);
        toast.success("Post created.");
      }
      setShow(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
  };
  const del = async (id) => { if (!confirm("Delete this post?")) return; await api.delete(`/admin/blog/${id}`); load(); };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">Editorial</p>
          <h1 className="font-serif text-4xl">Lookbook & Journal</h1>
        </div>
        <button onClick={openNew} className="bg-[#5C1A1B] text-[#FDFBF7] px-4 py-2 text-[11px] uppercase tracking-nav flex items-center gap-2" data-testid="admin-blog-new">
          <Plus size={14} /> New Post
        </button>
      </div>

      <div className="bg-white border border-black/5">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-nav text-[#7A7A7A] border-b border-black/5">
            <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Tags</th><th className="px-4 py-3">Published</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.post_id} className="border-b border-black/5" data-testid={`admin-blog-row-${p.slug}`}>
                <td className="px-4 py-3 font-serif text-base">{p.title}</td>
                <td className="px-4 py-3 font-mono-alt text-xs">{p.slug}</td>
                <td className="px-4 py-3 text-xs text-[#7A7A7A]">{p.tags?.join(", ")}</td>
                <td className="px-4 py-3">{p.published ? "Yes" : "Draft"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mr-4" data-testid={`admin-blog-edit-${p.slug}`}>Edit</button>
                  <button onClick={() => del(p.post_id)} className="text-[11px] uppercase tracking-nav text-[#D32F2F]"><Trash2 size={12} className="inline" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white max-w-3xl w-full my-8" data-testid="admin-blog-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
              <p className="font-serif text-2xl">{editingId ? "Edit post" : "New post"}</p>
              <button onClick={() => setShow(false)}><X /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Title</span>
                  <input required className="w-full border border-black/10 px-3 py-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Slug</span>
                  <input required className="w-full border border-black/10 px-3 py-2 font-mono-alt" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></label>
                <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-nav">Excerpt</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Author</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-nav">Tags (comma)</span>
                  <input className="w-full border border-black/10 px-3 py-2" value={form.tags.join(",")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-nav mb-2">Cover image</p>
                <ImageUploader
                  kind="blog"
                  max={1}
                  value={form.cover_image ? [form.cover_image] : []}
                  onChange={(arr) => setForm({ ...form, cover_image: arr[0] || "" })}
                  testIdPrefix="admin-blog-cover"
                />
              </div>
              <label className="block"><span className="text-[11px] uppercase tracking-nav">Body (HTML — write in paragraphs, use &lt;h2&gt;, &lt;p&gt;, &lt;img&gt;, &lt;blockquote&gt;)</span>
                <textarea rows="14" required className="w-full border border-black/10 px-3 py-2 font-mono-alt text-xs" value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} /></label>

              <ProductPicker
                selected={form.product_ids}
                onChange={(ids) => setForm({ ...form, product_ids: ids })}
              />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-blog-published" />
                Published
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t border-black/10">
                <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-[11px] uppercase tracking-nav">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#5C1A1B] text-white text-[11px] uppercase tracking-nav" data-testid="admin-blog-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
